import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

// ============ CLIENTS ============
export const useClients = (search = '') => {
  return useQuery({
    queryKey: ['clients', search],
    queryFn: async () => {
      const { data } = await api.get('/clients', { params: { search } });
      return data;
    },
  });
};

export const useClient = (id) => {
  return useQuery({
    queryKey: ['client', id],
    queryFn: async () => {
      const { data } = await api.get(`/clients/${id}`);
      return data;
    },
    enabled: !!id,
  });
};

// ============ SUPPLIERS ============
export const useSuppliers = (search = '') => {
  return useQuery({
    queryKey: ['suppliers', search],
    queryFn: async () => {
      const { data } = await api.get('/suppliers', { params: { search } });
      return data;
    },
  });
};

export const useSupplier = (id) => {
  return useQuery({
    queryKey: ['supplier', id],
    queryFn: async () => {
      const { data } = await api.get(`/suppliers/${id}`);
      return data;
    },
    enabled: !!id,
  });
};

// ============ TICKETS ============
export const useTickets = (params = {}) => {
  return useQuery({
    queryKey: ['tickets', params],
    queryFn: async () => {
      const { data } = await api.get('/tickets', { params });
      return data;
    },
  });
};

// ============ PAYMENTS ============
export const usePayments = (params = {}) => {
  return useQuery({
    queryKey: ['payments', params],
    queryFn: async () => {
      const { data } = await api.get('/payments', { params });
      return data;
    },
  });
};

// ============ DESTINATIONS ============
export const useDestinations = () => {
  return useQuery({
    queryKey: ['destinations'],
    queryFn: async () => {
      const { data } = await api.get('/destinations');
      return data;
    },
    staleTime: 1000 * 60 * 60, // 1 orë - ndryshon rrallë
  });
};

// ============ AIRLINES ============
export const useAirlines = () => {
  return useQuery({
    queryKey: ['airlines'],
    queryFn: async () => {
      const { data } = await api.get('/airlines');
      return data;
    },
    staleTime: 1000 * 60 * 60,
  });
};

// ============ BANK ACCOUNTS ============
export const useBankAccounts = () => {
  return useQuery({
    queryKey: ['bank-accounts'],
    queryFn: async () => {
      const { data } = await api.get('/bank-accounts');
      return data;
    },
  });
};

// ============ EXPENSES ============
export const useExpenses = (params = {}) => {
  return useQuery({
    queryKey: ['expenses', params],
    queryFn: async () => {
      const { data } = await api.get('/expenses', { params });
      return data;
    },
  });
};

export const useExpenseCategories = () => {
  return useQuery({
    queryKey: ['expense-categories'],
    queryFn: async () => {
      const { data } = await api.get('/expense-categories');
      return data;
    },
    staleTime: 1000 * 60 * 60,
  });
};

// ============ USERS ============
export const useUsers = () => {
  return useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const { data } = await api.get('/users');
      return data;
    },
  });
};

// ============ DASHBOARD ============
export const useDashboard = () => {
  return useQuery({
    queryKey: ['dashboard'],
    queryFn: async () => {
      const { data } = await api.get('/dashboard');
      return data;
    },
    staleTime: 1000 * 30, // 30 sekonda - dashboard duhet fresh
  });
};

// ============ REPORTS ============
export const useBalanceReport = (month) => {
  return useQuery({
    queryKey: ['report-balance', month],
    queryFn: async () => {
      const { data } = await api.get('/reports/balance', { params: { month } });
      return data;
    },
  });
};

export const useClientDebts = () => {
  return useQuery({
    queryKey: ['client-debts'],
    queryFn: async () => {
      const { data } = await api.get('/reports/client-debts');
      return data;
    },
  });
};

export const useSupplierDebts = () => {
  return useQuery({
    queryKey: ['supplier-debts'],
    queryFn: async () => {
      const { data } = await api.get('/reports/supplier-debts');
      return data;
    },
  });
};

// ============ INVALIDATE (për kur bëjmë ndryshime) ============
export const useInvalidate = () => {
  const queryClient = useQueryClient();
  
  return {
    invalidateClients: () => queryClient.invalidateQueries({ queryKey: ['clients'] }),
    invalidateSuppliers: () => queryClient.invalidateQueries({ queryKey: ['suppliers'] }),
    invalidateTickets: () => queryClient.invalidateQueries({ queryKey: ['tickets'] }),
    invalidatePayments: () => queryClient.invalidateQueries({ queryKey: ['payments'] }),
    invalidateDashboard: () => queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
    invalidateBankAccounts: () => queryClient.invalidateQueries({ queryKey: ['bank-accounts'] }),
    invalidateExpenses: () => queryClient.invalidateQueries({ queryKey: ['expenses'] }),
    invalidateAll: () => queryClient.invalidateQueries(),
  };
};
